'use client';

import 'dayjs/locale/ko';

import Button from '@enlighten/design-system/dist/Button';
import Divider from '@enlighten/design-system/dist/Divider';
import Grid from '@enlighten/design-system/dist/Grid';
import InputAdornment from '@enlighten/design-system/dist/InputAdornment';
import Select from '@enlighten/design-system/dist/Select';
import TextField from '@enlighten/design-system/dist/TextField';
import Typography from '@enlighten/design-system/dist/Typography';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { useAreaCode, useData } from '@/contexts/pages/market/MarketProvider';

dayjs.locale('ko');

interface CalculatorFormData {
	pv: string;
	weight: '0.7' | '1.0' | '1.2' | '1.5';
	result: number;
	pvResult: number;
	smpRecResult: number;
}

export default function Calculator() {
	const areaCode = useAreaCode();
	const data = useData();

	const { control, setValue, reset, handleSubmit } = useForm<CalculatorFormData>({
		mode: 'all',
		defaultValues: {
			weight: '1.0',
		},
	});

	const onSubmit = ({ pv, weight }: CalculatorFormData) => {
		if (data) {
			const pvPower = +pv.replace(/,/g, '');
			const smpRec = +((data[areaCode].smpLatest?.average || 0) + ((data[areaCode].recLatest?.averagePrice || 0) / 1000) * +weight).toFixed(2);
			setValue('result', pvPower * smpRec);
			setValue('pvResult', pvPower);
			setValue('smpRecResult', smpRec);
		}
	};

	return (
		<Grid container width="100%">
			<Typography fontSize={20} fontWeight={700} marginBottom="12px">
				수익 계산기 - {areaCode === '0' ? '통합' : areaCode === '1' ? '육지' : '제주'}
			</Typography>
			<Grid container alignItems="center" width="100%" marginBottom={1}>
				<Typography fontSize={16} color="grey.A400">
					오늘 발전량
				</Typography>
				<Typography color="grey.700" marginLeft={1}>
					{dayjs().locale('ko').format('YYYY.MM.DD (ddd)')}
				</Typography>
			</Grid>
			<Controller
				control={control}
				name="pv"
				rules={{
					required: '오늘 발전량을 입력해주세요',
				}}
				render={({ field: { value, onChange }, fieldState: { error } }) => (
					<TextField
						fullWidth
						placeholder="380"
						value={value || ''}
						onKeyDown={(e) => {
							if (e.key === '.' && value.includes('.')) {
								e.preventDefault();
							}
							if (!/[0-9.]/g.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onChange={(e) => {
							if (!/[ㄱ-힣]/g.test(e.target.value)) {
								if (e.target.value === '') {
									onChange(undefined);
								} else {
									const [decimal, float] = e.target.value.replace(/,/g, '').split('.');
									onChange(`${(+decimal).toLocaleString()}${e.target.value.includes('.') ? '.' : ''}${float || ''}`);
								}
							}
						}}
						slotProps={{
							input: {
								endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
							},
						}}
						sx={{
							'.MuiOutlinedInput-input': {
								fontSize: 16,
								textAlign: 'right',
								paddingY: '9.5px',
								MozAppearance: 'none',
								'::-webkit-outer-spin-button, ::-webkit-inner-spin-button': {
									WebkitAppearance: 'none',
									margin: 0,
								},
							},
							'.MuiFormHelperText-root': {
								opacity: 0,
								transformOrigin: '100% 0',
								transform: 'scaleY(0)',
								transition: 'all 0.15s ease',
								'&.Mui-error': {
									opacity: 1,
									transform: 'scaleY(100%)',
								},
							},
						}}
						error={!!error}
						helperText={error?.message ?? ' '}
					/>
				)}
			/>
			<Typography fontSize={16} color="grey.A400" marginBottom={1}>
				REC 가중치
			</Typography>
			<Controller
				control={control}
				name="weight"
				render={({ field: { value, onChange } }) => (
					<Select
						fullWidth
						IconComponent={ExpandMoreOutlined}
						value={value}
						onChange={(e) => {
							onChange(e.target.value);
						}}
						items={['0.7', '1.0', '1.2', '1.5'].map((value) => ({
							title: value,
							value,
							sx: {
								justifyContent: 'flex-end',
								fontSize: 16,
							},
						}))}
						sx={{
							fontSize: 16,
							textAlign: 'right',
							'.MuiSelect-select': {
								paddingY: '9.5px',
							},
						}}
					/>
				)}
			/>
			<Grid
				container
				gap={2}
				width="100%"
				marginTop="20px"
				sx={{
					'.MuiButton-root': {
						flex: 1,
					},
				}}
			>
				<Button
					variant="outlined"
					size="small"
					type="reset"
					onClick={() => {
						reset();
					}}
				>
					초기화
				</Button>
				<Button size="small" type="submit" onClick={handleSubmit(onSubmit)}>
					계산하기
				</Button>
			</Grid>
			<Divider sx={{ width: '100%', marginY: 3 }} />
			<Grid container justifyContent="flex-end">
				<Controller
					control={control}
					name="result"
					render={({ field: { value } }) => (
						<Typography fontSize={24} fontWeight={700}>
							{value != null ? `${Math.round(+value).toLocaleString()}.0` : '-'} 원
						</Typography>
					)}
				/>
			</Grid>
			<Divider sx={{ width: '100%', marginY: 3 }} />
			<Grid container justifyContent="space-between" alignItems="center" width="100%" marginBottom={2}>
				<Typography color="grey.700">발전량</Typography>
				<Controller
					control={control}
					name="pvResult"
					render={({ field: { value } }) => <Typography>{value != null ? `${(+value.toFixed(1)).toLocaleString()}${`${value}`.includes('.') ? '' : '.0'}` : '-'} kWh</Typography>}
				/>
			</Grid>
			<Grid container justifyContent="space-between" alignItems="center" width="100%" marginBottom={2}>
				<Typography color="grey.700">SMP + REC {'\u00d7'} 가중치</Typography>
				<Controller control={control} name="smpRecResult" render={({ field: { value } }) => <Typography>{value != null ? value.toFixed(2) : '-'} 원/kWh</Typography>} />
			</Grid>
		</Grid>
	);
}
